/**
 * PWA install affordance — captures the browser's install lifecycle *outside*
 * React so the app never misses the one-shot `beforeinstallprompt` event.
 * Chromium fires that event once, whenever it decides the app is installable,
 * which can land before any given component mounts; a module-level listener
 * (attached at first import — see the side-effect import in `main.tsx`) holds
 * onto it until the header `InstallButton` subscribes via `useSyncExternalStore`.
 *
 * iOS has no programmatic install (`beforeinstallprompt` doesn't exist in
 * WebKit), so there the button opens a guided Share → Add to Home Screen sheet
 * instead — see `isIos` and the `InstallButton` component.
 */

/**
 * The non-standard Chromium event, absent from the DOM lib types. Minimal shape
 * — just the members we use to drive the native install dialog.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<() => void>();

function notify(): void {
  for (const cb of subscribers) cb();
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Suppress the browser's own mini-infobar; we surface our own button.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  // Once installed, the captured event is spent and the button should vanish.
  window.addEventListener("appinstalled", () => {
    deferred = null;
    notify();
  });
}

/** Subscribe to install-state changes (for `useSyncExternalStore`). */
export function subscribeInstall(callback: () => void): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/** The captured prompt, or null if the app isn't (yet) installable here. */
export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferred;
}

/**
 * Fire the native install dialog. The captured event is single-use, so drop it
 * whatever the outcome — the button hides for this session, and Chromium
 * re-fires `beforeinstallprompt` on a later visit if the app is still
 * uninstalled. Returns `"unavailable"` if there was no prompt to fire.
 */
export async function promptInstall(): Promise<
  "accepted" | "dismissed" | "unavailable"
> {
  const event = deferred;
  if (!event) return "unavailable";
  // Clear the event *before* prompting: it's single-use, so a rapid second
  // click must see null and bail rather than call `prompt()` twice (which
  // throws NotAllowedError). Also hides the button immediately on click.
  deferred = null;
  notify();
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}

/** True when already running as an installed app (standalone display). */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari's non-standard flag, set when launched from the home screen.
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true
  );
}

/**
 * True on iPhone/iPad. Every iOS browser is WebKit and installs only via the
 * manual Share → Add to Home Screen flow, so this gates the guided sheet.
 */
export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports a desktop-Mac UA; touch points are the only tell.
  const iPadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || iPadOs;
}
